import { ChevronLeft, PinMark } from '@app/components/icons/ui-icons';
import { PeekingBunny } from '@app/components/ui/bunny';
import LazyImage from '@app/components/ui/lazy-image';
import PhotoViewer from '@app/components/ui/photo-viewer';
import { SwayingPlant } from '@app/components/ui/plant';
import { diary, profile } from '@app/content';
import Layout from '@app/layouts/_layout';
import { DiaryEntryDto } from '@app/models/dtos/contentDto';
import { youtubePoster } from '@app/utils/media';
import { formatDiaryDate, monthKey } from '@app/utils/stringUtils';
import { motion, useReducedMotion } from 'framer-motion';
import { NextSeo } from 'next-seo';
import Link from 'next/link';
import { useState } from 'react';

function Media({ entry }: { entry: DiaryEntryDto }) {
    const [playing, setPlaying] = useState(false);

    if (entry.type === 'youtube') {
        if (playing) {
            return (
                <iframe
                    src={`https://www.youtube-nocookie.com/embed/${entry.videoId}?autoplay=1&rel=0`}
                    title={entry.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute inset-0 h-full w-full border-0"
                />
            );
        }

        return (
            <button type="button" onClick={() => setPlaying(true)} className="group/play absolute inset-0" aria-label={`Play ${entry.title}`}>
                <LazyImage src={youtubePoster(entry.videoId)} alt={entry.title} fill sizes="340px" className="object-cover" loaderSize={26} />
                <span className="bg-paper/70 absolute left-1/2 top-1/2 h-11 w-11 -translate-x-1/2 -translate-y-1/2 rounded-full backdrop-blur-sm transition-colors group-hover/play:bg-accent" />
            </button>
        );
    }

    return <LazyImage src={entry.src} alt={entry.alt} fill sizes="(max-width: 640px) 100vw, 340px" className="object-cover" loaderSize={30} />;
}

function Entry({ entry, index, onOpen }: { entry: DiaryEntryDto; index: number; onOpen: () => void }) {
    const reduced = useReducedMotion();
    // Tall photos crop to a band so a run of cards keeps an even rhythm.
    const frameHeight = entry.wide ? 'h-[clamp(300px,46vw,460px)]' : 'h-[clamp(240px,34vw,340px)]';

    return (
        <motion.article
            className="mb-5 overflow-hidden rounded-[22px] bg-card"
            initial={{ opacity: 0, y: reduced ? 0 : 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: reduced ? 0.2 : 0.5, delay: reduced ? 0 : Math.min(index, 3) * 0.04, ease: [0.22, 1, 0.36, 1] }}
        >
            <button type="button" onClick={onOpen} className={`group relative block w-full cursor-zoom-in overflow-hidden bg-paper ${frameHeight}`} aria-label={`Open photo: ${entry.place}, ${formatDiaryDate(entry.date)}`}>
                <Media entry={entry} />
            </button>

            <div className="px-5 pb-5 pt-4">
                <p className="t-script-sm leading-none text-accent">
                    {formatDiaryDate(entry.date)}
                    {entry.timeOfDay && <span className="text-muted">, {entry.timeOfDay}</span>}
                </p>

                <p className="t-small mt-2.5 inline-flex items-center gap-1.5 text-muted">
                    <PinMark className="shrink-0 text-muted" />
                    {entry.place}
                </p>

                {entry.caption && (
                    <p className="t-body mt-2.5 text-body">
                        {entry.caption}
                        {entry.icon === 'plant' && <SwayingPlant className="ml-2 inline-block align-[-0.42em]" />}
                    </p>
                )}
            </div>
        </motion.article>
    );
}

export default function DiaryPage() {
    const [viewing, setViewing] = useState<number | null>(null);
    const photos = diary.filter((entry) => entry.type === 'photo');
    let lastMonth = '';

    return (
        <>
            <NextSeo title={`Diary · ${profile.name}`} description={`Photos and notes from wherever ${profile.name} happened to be working.`} />
            <Layout
                profile={profile}
                topRight={
                    <Link href="/" className="t-label group inline-flex items-center gap-1.5 text-muted transition-colors hover:text-accent">
                        <ChevronLeft size={13} className="transition-transform group-hover:-translate-x-0.5" />
                        Back
                    </Link>
                }
            >
                <header>
                    <h1 className="t-script relative inline-block text-accent">
                        diary
                        <PeekingBunny say="hop in!" className="absolute bottom-[0.12em] left-[calc(100%+18px)]" />
                    </h1>
                    <p className="t-body mt-5 max-w-md text-body">Sometimes I remember to take a photo of where I am working. This is what I have so far.</p>
                    {diary.length > 0 && (
                        <p className="t-small mt-3 text-muted">
                            {diary.length} {diary.length === 1 ? 'entry' : 'entries'}
                        </p>
                    )}
                </header>

                {diary.length === 0 ? (
                    <>
                        <div className="rule mb-12 mt-12" aria-hidden="true" />
                        <div className="flex items-end gap-4">
                            <span className="relative shrink-0">
                                <PeekingBunny say="soon!" size={38} duration={6} />
                            </span>
                            <p className="t-body pb-1 text-muted">Bunny has not posted anything yet.</p>
                        </div>
                    </>
                ) : (
                    <div className="mt-12">
                        {diary.map((entry, index) => {
                            const month = monthKey(entry.date);
                            const isNewMonth = month !== lastMonth;
                            lastMonth = month;

                            return (
                                <div key={entry.id}>
                                    {/* Sticks while its own month scrolls past, the way a journal keeps you oriented. */}
                                    {isNewMonth && (
                                        <h2 className="bg-paper/85 sticky top-0 z-10 -mx-6 mb-4 px-6 pb-3 pt-4 backdrop-blur-md">
                                            <span className="t-small font-bold text-ink">{month}</span>
                                        </h2>
                                    )}
                                    <Entry entry={entry} index={index} onOpen={() => setViewing(photos.findIndex((photo) => photo.id === entry.id))} />
                                </div>
                            );
                        })}
                    </div>
                )}
            </Layout>

            <PhotoViewer entries={photos} index={viewing} onClose={() => setViewing(null)} onMove={setViewing} />
        </>
    );
}
